import { useCallback, useEffect, useRef } from "react";

const AUTOSAVE_DELAY_MS = 600;

type PendingSave<TValues> = { values: TValues };

export const useDraftAutosave = <TValues>(
  save: (values: TValues) => Promise<void>,
): {
  schedule: (values: TValues) => void;
  cancelPending: () => void;
  flush: () => Promise<void>;
} => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<PendingSave<TValues> | null>(null);
  const failedRef = useRef<PendingSave<TValues> | null>(null);
  const errorRef = useRef<unknown>();
  const chainRef = useRef<Promise<void>>(Promise.resolve());
  const saveRef = useRef(save);
  saveRef.current = save;

  const clearTimer = useCallback(() => {
    if (!timerRef.current) return;
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const run = useCallback((): Promise<void> => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (!pending) return chainRef.current;
    const attempt = chainRef.current.then(async () => {
      try {
        await saveRef.current(pending.values);
        if (!pendingRef.current) {
          failedRef.current = null;
          errorRef.current = undefined;
        }
      } catch (error) {
        if (!pendingRef.current) failedRef.current = pending;
        errorRef.current = error;
        throw error;
      }
    });
    chainRef.current = attempt.catch(() => undefined);
    return attempt;
  }, []);

  const schedule = useCallback(
    (values: TValues) => {
      pendingRef.current = { values };
      failedRef.current = null;
      clearTimer();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        void run().catch(() => undefined);
      }, AUTOSAVE_DELAY_MS);
    },
    [clearTimer, run],
  );

  const cancelPending = useCallback(() => {
    clearTimer();
    pendingRef.current = null;
  }, [clearTimer]);

  const flush = useCallback(async () => {
    let promotedFailure = false;
    for (;;) {
      clearTimer();
      if (!promotedFailure && !pendingRef.current && failedRef.current) {
        pendingRef.current = failedRef.current;
        failedRef.current = null;
        promotedFailure = true;
      }
      if (pendingRef.current) {
        await run();
        continue;
      }
      const observedChain = chainRef.current;
      await observedChain;
      if (pendingRef.current || observedChain !== chainRef.current) continue;
      if (errorRef.current) throw errorRef.current;
      return;
    }
  }, [clearTimer, run]);

  useEffect(
    () => () => {
      clearTimer();
      if (!pendingRef.current && failedRef.current) {
        pendingRef.current = failedRef.current;
        failedRef.current = null;
      }
      void run().catch(() => undefined);
    },
    [clearTimer, run],
  );

  return { schedule, cancelPending, flush };
};
